/**
 * Pure standing-latency classifier: is the glass-to-glass delay a ROUTE floor or a STANDING
 * BUFFER?
 *
 * Judged on the LIVE reading (`g2gLastMs`), not on the calibrated floor. The floor is a
 * window minimum and therefore lags a buffer that is standing up by the whole window: in the
 * 2026-09-19 replay it sat at 19 ms for 30 s while the live reading climbed 136 -> 331 ms,
 * so a floor-based rule saw nothing until the floor itself had absorbed the buffer - which is
 * the exact failure this module exists to catch. Persistence is the detector's job (its hold),
 * not the floor's.
 *
 * From the live reading subtract everything the route and the decoder legitimately account
 * for - one-way propagation (half the RTT floor), the decoder's per-frame residency, and one
 * frame interval of playout quantisation - and what is left is the EXCESS: delay that no
 * meter attributes to anything. A small excess is distance. A large excess with a flat
 * playout trend, an empty standing queue and nil loss is frames standing still on the receive
 * side: congestion is not active, so congestion is not what is holding them.
 *
 * Rules (PHASE_3.md, amended at Phase 3 review):
 *  - unmeasured g2gLastMs or rttFloorMs -> 'unknown', excessMs null
 *  - excess = g2gLast - rttFloor/2 - (decodeMsPerFrame ?? 0) - (deliveredFps ? 1000/deliveredFps : 0)
 *  - excess < minExcessMs -> 'route'
 *  - excess >= minExcessMs AND |trend| <= flatTrendMs AND queue <= maxQueueMs AND loss <= maxLossPct -> 'standing-buffer'
 *  - otherwise (excess high but trend/queue/loss active) -> 'route' (congestion owns it)
 *
 * Absent trend / queue / loss readings are treated as NOT active: absence of a congestion
 * meter is not evidence of congestion (the contract omits rather than zero-fills), and a
 * standing buffer must not stay hidden behind a meter a guest cannot sample. The live reading
 * and the RTT floor are the two inputs that must be present; without them there is no verdict.
 * `g2gFloorMs` still rides the inputs for the host log.
 *
 * Browser-free and Node/Jest-safe.
 */

import {
  DEFAULT_STANDING_LATENCY_THRESHOLDS,
  type StandingLatencyInputs,
  type StandingLatencyThresholds,
  type StandingLatencyVerdict,
} from './standingLatencyContracts';

const UNKNOWN_VERDICT: StandingLatencyVerdict = { evidenceState: 'unavailable', kind: 'unknown', excessMs: null, depthHypothesis: 'undetermined' };

/**
 * The live reading's excess above everything the route and decoder account for, ms, or null
 * when the reading or the RTT floor is unmeasured. Exposed so the detector and the host log
 * can show the number the verdict was made from.
 */
export function standingLatencyExcessMs(inputs: StandingLatencyInputs): number | null {
  if (!isFinite(inputs.g2gLastMs) || !isFinite(inputs.rttFloorMs)) return null;
  const decodeMs = isFinite(inputs.decodeMsPerFrame) ? inputs.decodeMsPerFrame : 0;
  const frameIntervalMs = isFinite(inputs.deliveredFps) && inputs.deliveredFps > 0 ? 1000 / inputs.deliveredFps : 0;
  return inputs.g2gLastMs - inputs.rttFloorMs / 2 - decodeMs - frameIntervalMs;
}

/** Pure. The depth hypothesis is always 'undetermined' here; the detector judges it over time. */
export function classifyStandingLatency(
  inputs: StandingLatencyInputs,
  thresholds: StandingLatencyThresholds = DEFAULT_STANDING_LATENCY_THRESHOLDS,
): StandingLatencyVerdict {
  if (inputs.measurementFresh === false) return UNKNOWN_VERDICT;
  const evidenceState = inputs.measurementFresh === true ? 'tentative' as const : 'unavailable' as const;
  const excess = standingLatencyExcessMs(inputs);
  if (excess === null) return UNKNOWN_VERDICT;
  const excessMs = Math.round(excess * 10) / 10;
  if (excessMs < thresholds.minExcessMs) return { evidenceState, kind: 'route', excessMs, depthHypothesis: 'undetermined' };

  const trendFlat = !isFinite(inputs.playoutLatencyTrendMs) || Math.abs(inputs.playoutLatencyTrendMs) <= thresholds.flatTrendMs;
  const queueEmpty = !isFinite(inputs.standingQueueMs) || inputs.standingQueueMs <= thresholds.maxQueueMs;
  const lossNil = !isFinite(inputs.packetsLostPct) || inputs.packetsLostPct <= thresholds.maxLossPct;
  const kind = trendFlat && queueEmpty && lossNil ? 'standing-buffer' : 'route';
  return { evidenceState, kind, excessMs, depthHypothesis: 'undetermined' };
}

function isFinite(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
